import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import type { RootStore } from "./root.store";
import type { TProjectLmsResults } from "@/types/lms";

/** What a project's linked LMS content achieved, and the recompute behind the refresh
 *  button (plan §6.6, NG-775).
 *
 *  **Deliberately not optimistic**, which is the design system's stated exception rather
 *  than a lapse: refresh replaces the numbers with ones the server computes from stored
 *  attempts, so there is nothing correct for the client to render in the meantime. The
 *  caller blocks its button and shows a spinner — the endpoint is user-initiated and
 *  watched, which puts it in the 1–2s band where feedback is mandatory.
 *
 *  **A failed read is kept apart from an empty one.** `loadFailedByProject` exists because
 *  "no results" and "the read failed" look identical on screen and only one of them is a
 *  bug — the exact confusion this whole surface is built to avoid.
 */
export class ProjectLmsResultsStore {
  /** Keyed by project id: this surface is reachable from several projects in one session,
   *  and a single array would show the previous project's numbers for the frame before
   *  the fetch resolves. */
  resultsByProject: Record<string, TProjectLmsResults[]> = {};
  /** Which projects have answered at least once, so an empty array can be told apart from
   *  "not fetched yet" without inferring it from a falsy length. */
  loadedByProject: Record<string, boolean> = {};
  loadFailedByProject: Record<string, boolean> = {};
  isLoading = false;
  isRefreshing = false;

  private rootStore: RootStore;
  /** Latest-wins across both verbs. A slow `GET` must never land on top of the numbers a
   *  later refresh already computed, and vice versa. */
  private requestSeq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  resultsFor(projectId: string): TProjectLmsResults[] {
    return this.resultsByProject[projectId] ?? [];
  }

  hasLoaded(projectId: string): boolean {
    return Boolean(this.loadedByProject[projectId]);
  }

  loadFailed(projectId: string): boolean {
    return Boolean(this.loadFailedByProject[projectId]);
  }

  async fetchResults(slug: string, projectId: string): Promise<TProjectLmsResults[]> {
    const seq = ++this.requestSeq;
    this.isLoading = true;
    // A retry in flight is not a failure any more. Left set, the previous failure reads
    // as a settled answer for as long as the new request takes, so a consumer that tells
    // "failed" apart from "empty" shows the error it is about to replace rather than a
    // loading state (the cost-per-completion card did exactly that on remount).
    this.loadFailedByProject[projectId] = false;
    try {
      const results = await this.rootStore.lmsResultsService.listResults(slug, projectId);
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.resultsByProject[projectId] = results;
          this.loadedByProject[projectId] = true;
          this.loadFailedByProject[projectId] = false;
        });
      }
      return results;
    } catch (error) {
      // Breaks the user action — the tab has nothing to render — so `console.error`, with
      // the ids that make it triageable.
      console.error("[lms] project results fetch failed", { slug, projectId, error });
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.loadFailedByProject[projectId] = true;
        });
      }
      throw error;
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  /** Recompute from stored attempts and take the new numbers straight from the response.
   *
   *  The `POST` answers the same body as the `GET`, so this is one round trip rather than
   *  a recompute followed by a re-read.
   */
  async refresh(
    slug: string,
    projectId: string,
    source: AnalyticsSource = "page",
  ): Promise<TProjectLmsResults[]> {
    const seq = ++this.requestSeq;
    this.isRefreshing = true;
    try {
      const results = await this.rootStore.lmsResultsService.refreshResults(slug, projectId);
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.resultsByProject[projectId] = results;
          this.loadedByProject[projectId] = true;
          this.loadFailedByProject[projectId] = false;
        });
      }
      track("lms_project_results_refreshed", { project_id: projectId, source });
      return results;
    } catch (error) {
      // The numbers already on screen stay: they were true when they were computed, and
      // blanking them would punish the reader for a failed refresh.
      console.error("[lms] project results refresh failed", { slug, projectId, error });
      throw error;
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isRefreshing = false;
        });
      }
    }
  }

  reset(): void {
    this.resultsByProject = {};
    this.loadedByProject = {};
    this.loadFailedByProject = {};
    this.isLoading = false;
    this.isRefreshing = false;
  }
}
